import { useEffect } from 'react';

import { useProject } from '@contexts/Project.context';

/**
 * Unregisters a sidebar whose panel has nothing left to show.
 *
 * A panel that renders `null` while staying mounted runs no unmount teardown, so
 * its id stays in `activeSidebars` with nothing on screen — and SidebarTemplate
 * dims and disables a sidebar for as long as a child of it is registered, which
 * leaves the parent panel faded and unclickable (issue #482).
 *
 * Call it above the component's own `return null` guards, passing the same
 * condition those guards test. It never reopens a panel: content coming back is
 * not a reason to show one the user didn't ask for.
 *
 * @param {string} sidebarId
 * @param {boolean} hasContent - false once the panel has nothing to render
 */
const useCloseSidebarWhenEmpty = (sidebarId, hasContent) => {
	// `dispatch` rather than the context's `removeFromActiveSidebars`, which is a
	// new function on every context render and would re-run this effect with it.
	const { dispatch } = useProject();

	// Unguarded: closing an id that isn't registered leaves the state untouched.
	useEffect(() => {
		if (hasContent) return;
		dispatch({ type: 'removeActiveSidebar', payload: sidebarId });
	}, [sidebarId, hasContent, dispatch]);
};

export default useCloseSidebarWhenEmpty;
